import { Fragment, memo, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/Hooks";
import { logout, setPdfExportObject, toUnLoading } from "../../store/SiteSlice";
import { useTranslation } from "react-i18next";
import { mdlReservationListRequest } from "../../models/service-models/reservation/ReservationListRequest";
import { useMutation, useQuery } from "react-query";
import { ReservationService } from "../../services/ReservationService";
import { ExpandableCustomerList } from "../../components/reservation/ExpandableCustomerList";
import {
    Avatar,
    Badge,
    Button, Card, Col, Flex, Row, Skeleton, Space, Spin, message
} from "antd";
import { SFModal } from "../../components/elements/SFModal";
import { ReservationMonitorTable } from "../../components/reservation/ReservationMonitorTable";
import { ReservationDetail } from "../../components/reservation/ReservationDetail";
import { mdlReservation } from "../../models/domain/Reservation";
import { SyncOutlined, CheckOutlined, CloseOutlined, SearchOutlined, PrinterOutlined, ReloadOutlined, HistoryOutlined, PrinterFilled } from '@ant-design/icons';
import { MailIcon, PdfIcon } from "../../components/common/CustomIcons";
import { ReservationConfirm } from "../../components/reservation/ReservationConfirm";
import { toast } from "react-toastify";
import { ReservationReject } from "../../components/reservation/ReservationReject";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import _ from "lodash";
import { mdlPDFRendererProps } from "../../models/ui-models/PDFRendererProps";
import { enmPdfRendererType } from "../../models/enums/PdfRenderer";
import { enmPdfExportType } from "../../models/enums/PdfExportType";
import ReservationMail from "../../components/reservation/ReservationMail";
import { ReservationFilter } from "../../components/reservation/ReservationFilters";
import { enmReservationType } from "../../models/enums/ReservationType";
import { enmReservationStatus } from "../../models/enums/ReservationStatus";
import { enmCountType } from "../../models/enums/CountType";
import { ResMonSortColumnTable } from "../../components/reservation/ReservationMonitorColumnSortableTable";
import { nanoid } from "@reduxjs/toolkit";
import { ExportModal } from "../../components/common/ExportModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { HotelWebParams } from "../../models/enums/HotelWebParams";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { mdlReservationPrintDetail } from "../../models/ui-models/ReservationPrintDetail";
import { KEY_MONITOR_CONFIRM, KEY_MONITOR_DETAIL, KEY_MONITOR_EMAIL, KEY_MONITOR_HISTORY, KEY_MONITOR_NOT_CONFIRM, KEY_MONITOR_PDF, KEY_MONITOR_PDF_LIST, KEY_MONITOR_PRINT, ReservationContextMenu } from "../../components/reservation/ReservationContextMenu";
import { ReservationHistory } from "../../components/reservation/ReservationHistory";
import { enmExcelExportType } from "../../utils/ExcelColumnMappings";

const disableReservationOperations = process.env.REACT_APP_DISABLE_RESERVATION_OPERATIONS === "true";

export const Monitor = memo(function Monitor() {
    const AUTO_SYNC_DEFAULT_IN_SECONDS = 90;
    const BADGE_OVERFLOW_COUNT = 999999;

    const dispatch = useAppDispatch();
    const userInfo = useAppSelector(x => x.site.user);
    const userHotels = useAppSelector(x => x.site.userHotels);
    const crossAgencies = useAppSelector(x => x.site.crossAgencies);
    const { t } = useTranslation();
    const [request, setRequest] = useState<mdlReservationListRequest | undefined>();
    const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
    const [openOrganizeModal, setOpenOrganizeModal] = useState<boolean>(false);
    const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
    const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
    const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
    const [openReservationMailModal, setOpenReservationMailModal] = useState<boolean>(false);
    const [selectedReservation, setSelectedReservation] = useState<string>();
    const [selectedReservations, setSelectedReservations] = useState<mdlReservation[]>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>();
    const [autoSyncStatus, setAutoSyncStatus] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [countdown, setCountdown] = useState(AUTO_SYNC_DEFAULT_IN_SECONDS);
    const [openModalExcelExport, setOpenModalExcelExport] = useState<boolean>(false);
    const [openReservationHistory, setOpenReservationHistory] = useState<boolean>(false);
    const [pdfExportIsLoading, setPdfExportIsLoading] = useState<boolean>(false);
    const [excelExportData, setExcelExportData] = useState<Array<mdlReservation>>([]);

    useEffect(() => {
        dispatch(toUnLoading());

        if (!data) {
            const nonPaymentHotels = userHotels?.filter(x => x.requirePayment === false) || [];
            const hotelIds = nonPaymentHotels.map(x => x.id);

            var req = {
                hotelIds,
                pageNumber: 1,
                pageSize: 10,
                paximumIds: nonPaymentHotels.map(x => Number(x.paximumId)),
                agencyIds: crossAgencies?.map(x => x.id) || [],
                confirmationStatuses: [enmConfirmationStatus.Wait],
                reservationTypes: [enmReservationType.New, enmReservationType.Changed, enmReservationType.Cancelled],
                reservationStatuses: [enmReservationStatus.Ok, enmReservationStatus.OnRequest, enmReservationStatus.NoShow, enmReservationStatus.GoShow, enmReservationStatus.Option]
            } as mdlReservationListRequest;

            const singleHotelId = hotelIds.length === 1 ? hotelIds[0] : null;
            if (singleHotelId) {
                const singleHotel = nonPaymentHotels.find(h => h.id === singleHotelId);

                const hasAutoListParam = singleHotel?.hotelWebParameters?.some(
                    p => p.parameterName === HotelWebParams[HotelWebParams.ListResAutomatically] && p.value1 === "TRUE"
                );

                if (!hasAutoListParam) {
                    req = { ...req, confirmationStatuses: [] };
                }
            }

            setRequest(req);
        }
    }, []);

    useEffect(() => {
        if (autoSyncStatus) {
            const interval = setInterval(() => {
                setCountdown(prev => (prev > 0 ? prev - 1 : AUTO_SYNC_DEFAULT_IN_SECONDS));
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [autoSyncStatus]);


    //#region API CALLS
    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getReservations", request],
        () => {
            if (request?.paximumIds)
                return ReservationService.List(request);
            else {
                toast.error(t("common.needALogin"));
                setTimeout(redirectToLogin, 3000);
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!request,
            refetchInterval: autoSyncStatus ? AUTO_SYNC_DEFAULT_IN_SECONDS * 1000 : false,
            cacheTime: 0,
            staleTime: 0,
            onSuccess: () => {
                if (autoSyncStatus) {
                    setCountdown(AUTO_SYNC_DEFAULT_IN_SECONDS);
                }
            }
        }
    );

    const { mutateAsync: getMultiResDetail, isLoading: resDetailLoading, data: resDetail } = useMutation(
        (request: mdlReservationPrintDetail) => ReservationService.MultiDetail(request.request!),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    if (variables.callbackFn) {
                        variables.callbackFn(data.body?.reservations);
                    }
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );


    const redirectToLogin = () => {
        dispatch(logout());
    }
    //#endregion

    //#region TABLE HANDLERS
    const handleTableParamsChange = (tableFilterSortRequest: mdlReservationListRequest) => {
        if (tableFilterSortRequest) {
            setRequest({
                ...request,
                ...tableFilterSortRequest
            });
        }
    }

    const handlePaginationChange = (page: number, pageSize: number) => {
        setRequest({ ...request, pageSize: pageSize ?? 10, pageNumber: page ?? 1 })
    }

    const handleExpandRowRenderer = (reservationId: string, isExpanded: boolean) => {
        var foundResObj = _.find(data?.body?.data, function (d) { return d.reservationId == reservationId });
        return <ExpandableCustomerList reservation={foundResObj} isExpanded={isExpanded} />
    }

    const handleRowDoubleClicked = (reservationId: string) => {
        setSelectedReservation(reservationId);
        var foundResObj = _.find(data?.body?.data, function (d) { return d.reservationId == reservationId });
        if (foundResObj)
            setSelectedReservations([foundResObj])
        setOpenDetailModal(true);
    }

    const handleDetailClick = () => {
        if (selectedReservations && selectedReservations.length == 1)
            setOpenDetailModal(true);
    }

    const onRowSelect = (indexes: string[], reservations: mdlReservation[]) => {
        if (indexes.length == 0) {
            setSelectedReservations(undefined);
            setSelectedRowKeys([]);
        }
        else {
            var res = _.filter(reservations, function (res) { return res != undefined });
            setSelectedReservations(res);
            setSelectedRowKeys(res.map((x => x.reservationId!)));
        }

    }
    //#endregion

    //#region HELPERS 
    const exportToPDF = (type: enmPdfExportType, reservations: Array<mdlReservation>, pdfRenderer?: enmPdfRendererType) => {
        var exportProps: mdlPDFRendererProps = {
            params: { reservations: reservations },
            rendererType: pdfRenderer,
            type: type
        }
        dispatch(setPdfExportObject(exportProps));
        setPdfExportIsLoading(false);

    }

    const handleFilterSearch = (values: mdlReservationListRequest) => {
        values.pageNumber = 1;
        values.pageSize = request?.pageSize;
        values.confirmationStatuses = values.confirmationStatuses?.map(x => Number(x));
        values.reservationStatuses = values.reservationStatuses?.map(x => Number(x));
        values.reservationTypes = values.reservationTypes?.map(x => Number(x));
        values.reqId = nanoid();
        setRequest(values);
        setAutoSyncStatus(false);
        setOpenFilterModal(!openFilterModal);
    }

    const isReservationConfirmedOrRejected = (reservation: mdlReservation) => (reservation.confirmationStatus == enmConfirmationStatus.Yes || reservation.confirmationStatus == enmConfirmationStatus.No);

    const isValidForOperation = () => {
        var result = false;
        var notValidReservations = _.filter(selectedReservations, isReservationConfirmedOrRejected);
        if (notValidReservations.length > 0) {
            var voucherNos = notValidReservations.map(x => x.voucherNo);
            var message = `${t("resTable.notValidForConfirmOrReject")} [${voucherNos.join(",")}]`;
            messageApi.open({
                type: "error",
                content: message,
                duration: 5,
            });
        }
        else {
            result = true;
        }
        return result;
    }
    //#endregion

    //#region CALLBACKS
    const handleSortOrderCallback = () => {
        setOpenOrganizeModal(false);
    }

    const handleConfirmCallback = () => {
        setSelectedReservation(undefined);
        setSelectedReservations(undefined);
        toast.success(t("resTable.confirmationSent"));
        setOpenConfirmModal(false);
        setOpenDetailModal(false);
        var pRequest = _.cloneDeep(request);
        if (pRequest != null) {
            pRequest.pageNumber = 1;
            pRequest.reqId = nanoid();
        }
        setRequest(pRequest);
        refetch();
    }

    const handleRejectCallback = () => {
        setSelectedReservation(undefined);
        setSelectedReservations(undefined);
        toast.success(t("resTable.rejectionSent"));
        setOpenRejectModal(false);
        setOpenDetailModal(false);
        var pRequest = _.cloneDeep(request);
        if (pRequest != null) {
            pRequest.pageNumber = 1;
            pRequest.reqId = nanoid();
        }
        setRequest(pRequest);
    }

    const handleMailCallback = () => {
        toast.success(t("resTable.mailSent"));
        setOpenReservationMailModal(false);
    }
    //#endregion

    //#region BUTTON HANDLERS
    const handleAutoSyncChange = (opt: boolean) => {
        setAutoSyncStatus(opt);
        //refetch();
    }

    const handleConfirmClick = () => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            var isValid = isValidForOperation();
            if (isValid)
                setOpenConfirmModal(true);
        }
    }

    const handleOuterConfirmClick = (reservation: mdlReservation) => {
        if (!reservation) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            var isValid = isValidForOperationOuter(reservation);
            if (isValid) {
                setSelectedReservations([reservation])
                setOpenConfirmModal(true);
            }
        }
    }

    const isValidForOperationOuter = (reservation: mdlReservation) => {
        var result = false;
        if (reservation.confirmationStatus == enmConfirmationStatus.Yes
            ||
            reservation.confirmationStatus == enmConfirmationStatus.No) {
            var message = `${t("resTable.notValidForConfirmOrReject")} [${reservation.voucherNo}]`;
            messageApi.open({
                type: "error",
                content: message,
                duration: 5,
            });
        }
        else {
            result = true;
        }
        return result;
    }

    const handleRejectClick = () => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            var isValid = isValidForOperation();
            if (isValid)
                setOpenRejectModal(true);
        }
    }

    const handlePrintPreview = (pdfRenderer: enmPdfRendererType) => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            setPdfExportIsLoading(true);
            getMultiResDetail({
                request: {
                    reservations: selectedReservations!.map(h => Object.assign(h!, {
                        reservationId: h.reservationId!,
                        agencyId: h.agencyId!,
                        paxId: h.paximumId!
                    }))
                }
                , callbackFn: (reservations?: Array<mdlReservation>) => handlePrint(reservations)
            })
        }
    }

    const handlePrintByRoom = (pdfRenderer: enmPdfRendererType) => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            setPdfExportIsLoading(true);
            getMultiResDetail({
                request: {
                    reservations: selectedReservations!.map(h => Object.assign(h!, {
                        reservationId: h.reservationId!,
                        agencyId: h.agencyId!,
                        paxId: h.paximumId!
                    }))
                }
                , callbackFn: (reservations?: Array<mdlReservation>) => handlePrintByRoomExport(reservations, pdfRenderer)
            })
        }
    }

    const handlePrintByRoomExport = (reservations?: Array<mdlReservation>, pdfRenderer?: enmPdfRendererType) => {
        if (reservations) {
            exportToPDF(enmPdfExportType.PRINT, reservations, pdfRenderer);
        }
    }

    const handleExportToExcelClick = () => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            setPdfExportIsLoading(true);
            getMultiResDetail({
                request: {
                    reservations: selectedReservations!.map(h => Object.assign(h!, {
                        reservationId: h.reservationId!,
                        agencyId: h.agencyId!,
                        paxId: h.paximumId!
                    }))
                }
                , callbackFn: (reservations?: Array<mdlReservation>) => processExcelExportData(reservations)
            })
        }
        setOpenModalExcelExport(true);
    }

    const processExcelExportData = (reservations?: Array<mdlReservation>) => {
        if (reservations) {
            setExcelExportData(reservations);
            setOpenModalExcelExport(true);
        }
    }

    const handlePrint = (reservations?: Array<mdlReservation>) => {
        if (reservations)
            exportToPDF(enmPdfExportType.PRINT, reservations, reservations.length > 1 ? enmPdfRendererType.ReservationList : enmPdfRendererType.ReservationVoucher);
    }

    const handleOuterPrintPreview = (reservation?: mdlReservation) => {
        setPdfExportIsLoading(true);
        if (reservation)
            exportToPDF(enmPdfExportType.PRINT, [reservation], enmPdfRendererType.ReservationVoucher);
    }

    const handleMultiPrintPreview = (reservations?: Array<mdlReservation>) => {
        if (reservations)
            exportToPDF(enmPdfExportType.PRINT, reservations);
    }

    const handleOuterExportToPDF = (reservation?: mdlReservation) => {
        if (reservation)
            exportToPDF(enmPdfExportType.PDF, [reservation], enmPdfRendererType.ReservationVoucher);
    }

    const handleExportToPDF = (pdfRenderer: enmPdfRendererType) => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            getMultiResDetail({
                request: {
                    reservations: selectedReservations!.map(h => Object.assign(h!, {
                        reservationId: h.reservationId!,
                        agencyId: h.agencyId!,
                        paxId: h.paximumId!
                    }))
                }
                , callbackFn: (reservations?: Array<mdlReservation>) => handleMultiExportPdf(reservations, pdfRenderer)
            })
        }
    }

    const handleMultiExportPdf = (reservations?: Array<mdlReservation>, pdfRenderer?: enmPdfRendererType) => {
        if (reservations)
            exportToPDF(enmPdfExportType.PDF, reservations, pdfRenderer);
    }

    const handleSendMailClick = () => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            setOpenReservationMailModal(true);
        }
    }

    const handleHistoryClick = () => {
        if (!selectedReservations || (selectedReservations && selectedReservations.length == 0)) {
            messageApi.open({
                type: "warning",
                content: t("resTable.chooseAtLeastOneReservation"),
                duration: 5,
            });
        }
        else {
            setOpenReservationHistory(true);
        }
    }

    const handleShortCutClick = (type: enmCountType, count?: number) => {
        var pRequest = _.cloneDeep(request);
        if (pRequest) {
            switch (type) {
                case enmCountType.CONFIRM:
                    pRequest.confirmationStatuses = [enmConfirmationStatus.Yes];
                    break;
                case enmCountType.NOTCONFIRM:
                    pRequest.confirmationStatuses = [enmConfirmationStatus.No];
                    break;
                case enmCountType.WAIT:
                    pRequest.confirmationStatuses = [enmConfirmationStatus.Wait];
                    break;
                case enmCountType.CANCEL:
                    pRequest.reservationTypes = [enmReservationType.Cancelled];
                    break;
                case enmCountType.CHANGED:
                    pRequest.reservationTypes = [enmReservationType.Changed];
                    break;
                case enmCountType.NEW:
                    pRequest.reservationTypes = [enmReservationType.New];
                    break;
                case enmCountType.WARNING:
                    pRequest = {
                        hotelIds: userHotels?.filter(x => x.requirePayment == false).map(x => x.id),
                        pageNumber: 1,
                        pageSize: 10,
                        paximumIds: userHotels?.filter((x: any) => x.requirePayment == false).map(x => Number(x.paximumId)),
                        agencyIds: crossAgencies?.map(x => x.id!),
                        hasWarning: true
                    }
                    break;
            }
            setRequest(pRequest);
        }
    }

    const handleDetailModalToggle = () => {
        if (openDetailModal) {
            if (selectedRowKeys && selectedRowKeys.length > 0) {
                var reservations = _.filter(data?.body?.data, function (res) { return selectedRowKeys.includes(res.reservationId!) });
                if (reservations.length == 0) setSelectedReservation(_.first(reservations)?.reservationId!);
                setSelectedReservations(reservations);
            } else {
                setSelectedReservation(undefined);
                setSelectedReservations(undefined);
            }
        }
        setOpenDetailModal(!openDetailModal)
    }

    const handleContextMenuClick = (menuKeyId?: string, reservation?: mdlReservation) => {
        if (!menuKeyId || !reservation) {
            return;
        }

        setSelectedReservations([reservation]);
        setSelectedRowKeys([reservation.reservationId!]);

        switch (menuKeyId) {
            case KEY_MONITOR_CONFIRM:
                if (!isReservationConfirmedOrRejected(reservation)) {
                    setOpenConfirmModal(true);
                } else {
                    toast.error(t("resTable.reservationIsAlreadyConfirmedOrRejected"));
                }
                break;
            case KEY_MONITOR_NOT_CONFIRM:
                if (!isReservationConfirmedOrRejected(reservation)) {
                    setOpenRejectModal(true);
                }
                else {
                    toast.error(t("resTable.reservationIsAlreadyConfirmedOrRejected"));
                }
                break;
            case KEY_MONITOR_DETAIL:
                setOpenDetailModal(true);
                break;
            case KEY_MONITOR_PDF:
                getMultiResDetail({
                    request: {
                        reservations:
                            [
                                {
                                    reservationId: reservation.reservationId!,
                                    agencyId: reservation.agencyId!,
                                    paxId: reservation.paximumId!
                                }
                            ]
                    }
                    , callbackFn: (reservations?: Array<mdlReservation>) => handleMultiExportPdf(reservations, enmPdfRendererType.ReservationVoucher)
                });
                break;
            case KEY_MONITOR_PDF_LIST:
                getMultiResDetail({
                    request: {
                        reservations: [
                            {
                                reservationId: reservation.reservationId!,
                                agencyId: reservation.agencyId!,
                                paxId: reservation.paximumId!
                            }
                        ]
                    }
                    , callbackFn: (reservations?: Array<mdlReservation>) => handleMultiExportPdf(reservations, enmPdfRendererType.ReservationList)
                });
                break;
            case KEY_MONITOR_PRINT:
                getMultiResDetail({
                    request: {
                        reservations: [
                            {
                                reservationId: reservation.reservationId!,
                                agencyId: reservation.agencyId!,
                                paxId: reservation.paximumId!
                            }
                        ]
                    }
                    , callbackFn: handleMultiPrintPreview
                });
                break;
            case KEY_MONITOR_EMAIL:
                setOpenReservationMailModal(true);
                break;
            case KEY_MONITOR_HISTORY:
                setOpenReservationHistory(true);
                break;
            default:
                break;
        }
    }
    //#endregion

    return (
        <>
            {contextHolder}
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <Spin spinning={resDetailLoading}>
                            <div className="nk-content-body">
                                <div className="nk-block">
                                    <div className="row g-gs">
                                        <div className="col-12">
                                            <Card className="card card-bordered h-100">
                                                <Row style={{ width: "100%" }}>
                                                    {
                                                        isLoading
                                                        &&
                                                        <>
                                                            <Skeleton active />
                                                        </>
                                                    }
                                                    {
                                                        data
                                                        &&
                                                        <Fragment>
                                                            <Col span={20}>
                                                                <Row gutter={18}>
                                                                    <Col>
                                                                        <Button
                                                                            onClick={() => setOpenFilterModal(!openFilterModal)}
                                                                            type="primary" icon={<SearchOutlined />} size="middle">
                                                                            {t("monitor.searchFilters")}
                                                                        </Button>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="yellow" count={data.body?.waitCount ?? 0} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.WAIT, data.body?.waitCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.wait")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="green" count={data.body?.confirmCount} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.CONFIRM, data.body?.confirmCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.confirm")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="red" count={data.body?.notConfirmCount ?? 0} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.NOTCONFIRM, data.body?.notConfirmCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.notConfirm")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="blue" count={data.body?.newCount ?? 0} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.NEW, data.body?.newCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.new")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="blue" count={data.body?.cancelCount ?? 0} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.CANCEL, data.body?.cancelCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.cancel")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="blue" count={data.body?.changedCount ?? 0} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.CHANGED, data.body?.changedCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.change")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                    <Col>
                                                                        <Badge showZero color="tomato" count={data.body?.warningCount ?? 0} overflowCount={BADGE_OVERFLOW_COUNT}>
                                                                            <Button onClick={() => handleShortCutClick(enmCountType.WARNING, data.body?.warningCount)} style={{ backgroundColor: "white", color: "black" }} type="default">{t("monitor.warnings")}</Button>
                                                                        </Badge>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                            <Col span={4}>
                                                                <Flex vertical justify="end" align="end">
                                                                    <Button
                                                                        onClick={() => setOpenOrganizeModal(!openOrganizeModal)}
                                                                        type="primary" size="middle">
                                                                        {t("monitor.organizeColumns")}
                                                                    </Button>
                                                                </Flex>
                                                            </Col>
                                                        </Fragment>


                                                    }
                                                </Row>
                                            </Card>
                                        </div>
                                        <div className="col-12">
                                            <div className="card card-bordered h-100">
                                                <div className="card-inner">
                                                    {
                                                        isLoading
                                                        &&
                                                        <>
                                                            <Skeleton active />
                                                            <Skeleton active />
                                                            <Skeleton active />
                                                        </>
                                                    }
                                                    {
                                                        data
                                                        &&
                                                        <ReservationMonitorTable
                                                            onRowSelection={onRowSelect}
                                                            selectedRowKeys={selectedRowKeys}
                                                            onRowDoubleClicked={handleRowDoubleClicked}
                                                            expandRowRenderer={handleExpandRowRenderer}
                                                            onPaginationChange={handlePaginationChange}
                                                            onContextMenuItemClick={handleContextMenuClick}
                                                            pagination={{
                                                                align: "center",
                                                                current: data?.body?.pageNumber,
                                                                pageSizeOptions: [10, 20, 50],
                                                                defaultPageSize: 10,
                                                                pageSize: request?.pageSize,
                                                                defaultCurrent: 1,
                                                                responsive: true,
                                                                showSizeChanger: true,
                                                                total: data?.body?.totalCount,
                                                                showTotal: (total: number, range: [number, number]) => t("pagination.total") + ` ${data?.body?.totalCount} ` + t("pagination.result"),
                                                                locale: { items_per_page: "/ " + t("pagination.itemPerPage") }
                                                            }}
                                                            onTableParamsChange={handleTableParamsChange}
                                                            reservationListRequest={request}
                                                            dataSource={data?.body?.data}
                                                            tableTools={
                                                                <Flex gap="small" wrap justify="center">
                                                                    {!disableReservationOperations && (
                                                                        <>
                                                                            <Button
                                                                                onClick={handleConfirmClick}
                                                                                type="primary" className="btn-success" icon={<CheckOutlined />} size="middle"
                                                                            >
                                                                                {t("resTable.confirm")}
                                                                            </Button>
                                                                            <Button
                                                                                onClick={handleRejectClick}
                                                                                type="primary" danger icon={<CloseOutlined />} size="middle">
                                                                                {t("resTable.reject")}
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                    <Button onClick={handleDetailClick} type="primary" disabled={(selectedReservations && selectedReservations.length > 1) || !selectedReservations} icon={<SearchOutlined />} size="middle">
                                                                        {t("resTable.detail")}
                                                                    </Button>
                                                                    <Button type="primary" onClick={() => handleExportToPDF(enmPdfRendererType.ReservationVoucher)} icon={<PdfIcon />} size="middle">
                                                                        {t("resTable.exportToPDF")}
                                                                    </Button>
                                                                    <Button type="primary" onClick={() => handleExportToPDF(enmPdfRendererType.ReservationList)} icon={<PdfIcon />} size="middle">
                                                                        {t("resTable.exportToPDFAsList")}
                                                                    </Button>
                                                                    <Button type="primary" onClick={handleExportToExcelClick} icon={<FontAwesomeIcon icon={faFileExcel} />} size="middle">
                                                                        {t("resTable.exportToExcel")}
                                                                    </Button>
                                                                    <Button
                                                                        onClick={() => handlePrintByRoom(enmPdfRendererType.ReservationVoucher)}
                                                                        type="primary" icon={<PrinterOutlined />} size="middle">
                                                                        {t("resTable.printByRoom")}
                                                                    </Button>
                                                                    <Button
                                                                        onClick={() => handlePrintPreview(enmPdfRendererType.ReservationVoucher)}
                                                                        type="primary" icon={<PrinterFilled />} size="middle">
                                                                        {t("resTable.print")}
                                                                    </Button>
                                                                    <Button type="primary" onClick={handleSendMailClick} icon={<MailIcon />} size="middle">
                                                                        {t("resTable.email")}
                                                                    </Button>
                                                                    <Button type="primary" onClick={handleHistoryClick} icon={<HistoryOutlined />} size="middle">
                                                                        {t("resTable.history")}
                                                                    </Button>
                                                                    {
                                                                        autoSyncStatus
                                                                            ?
                                                                            <Button type="primary" className="btn-success" onClick={() => handleAutoSyncChange(false)} icon={<SyncOutlined />} size="middle">
                                                                                {t("resTable.autoSyncOn")} - {`${countdown} ${t("common.sec")}`}
                                                                            </Button>
                                                                            :
                                                                            <Button type="primary" danger onClick={() => handleAutoSyncChange(true)} icon={<ReloadOutlined />} size="middle">
                                                                                {t("resTable.autoSyncOff")}
                                                                            </Button>
                                                                    }
                                                                </Flex>
                                                            }
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Spin>
                    </div>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 80 / 100}
                open={openDetailModal}
                toggler={() => { handleDetailModalToggle() }}
                body={<ReservationDetail onExport={handleOuterExportToPDF} onPrint={handleOuterPrintPreview} onConfirmClick={handleOuterConfirmClick} onRejectClick={handleRejectClick} reservation={_.first(selectedReservations)} />}
                title={t("reservationDetail.title")}
            />
            <SFModal
                width={window.innerWidth * 99 / 100}
                open={openConfirmModal}
                toggler={() => { setOpenConfirmModal(!openConfirmModal) }}
                body={<ReservationConfirm callback={handleConfirmCallback} open={openConfirmModal} reservations={selectedReservations} closeCallBack={() => setOpenConfirmModal(false)} />}
                title={t("resConfirm.title")}
            />
            <SFModal
                width={window.innerWidth * 99 / 100}
                open={openRejectModal}
                toggler={() => { setOpenRejectModal(!openRejectModal) }}
                body={<ReservationReject callback={handleRejectCallback} open={openConfirmModal} reservations={selectedReservations} closeCallBack={() => setOpenConfirmModal(false)} />}
                title={t("resReject.title")}
            />
            <SFModal
                width={window.innerWidth * 25 / 100}
                open={openReservationMailModal}
                toggler={() => { setOpenReservationMailModal(!openReservationMailModal) }}
                body={<ReservationMail callback={handleMailCallback} reservations={selectedReservations} />}
                title={t("resMail.title")}
            />

            <SFModal
                width={window.innerWidth * 55 / 100}
                open={openFilterModal}
                toggler={() => { setOpenFilterModal(!openFilterModal) }}
                body={<ReservationFilter onSearch={handleFilterSearch} form={request} />}
                title={t("monitor.searchFilters")}
            />

            <SFModal
                width={window.innerWidth * 95 / 100}
                open={openOrganizeModal}
                toggler={() => { setOpenOrganizeModal(!openOrganizeModal) }}
                body={<ResMonSortColumnTable callBack={handleSortOrderCallback} />}
                title={t("monitor.searchFilters")}
            />
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalExcelExport}
                toggler={() => { setOpenModalExcelExport(!openModalExcelExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.ResMon} name="resmon" data={excelExportData} fnModalClose={() => setOpenModalExcelExport(!openModalExcelExport)} />}
                title={t("common.exportToExcel")}
            />
            <SFModal
                width={window.innerWidth * 85 / 100}
                open={openReservationHistory}
                toggler={() => { setOpenReservationHistory(!openReservationHistory) }}
                body={<ReservationHistory reservationId={selectedReservations && selectedReservations.length > 0 ? selectedReservations[0].reservationId : undefined} />}
                title={t("monitor.reservationHistory")}
            />
        </>

    );
});
